import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Text,
  HStack,
  IconButton,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  Spinner,
  Select,
} from "@chakra-ui/react";
import { FaEye, FaTrash } from "react-icons/fa";
import { Link } from "react-router-dom";
import axiosInstance from "../../../config/axiosInstance";
import OTPModal from "../OtpModal";
import {jwtDecode} from "jwt-decode";

const OrdersTable = ({ data = [], fetchOrders, orderLoading }) => {
  const toast = useToast();
  const cancelRef = useRef();

  const [deleteState, setDeleteState] = useState({
    isOpen: false,
    loading: false,
    orderId: null,
  });

  const [otpModal, setOtpModal] = useState({
    open: false,
    type: "delivery",
    orderId: null,
    orderDetailId: null,
    email: "",
    otp: "",
    loading: false,
    resendLoading: false,
    timeLeft: 60,
    verified: false,
  });

  const [bulkOtpModal, setBulkOtpModal] = useState({
    open: false,
    orderId: null,
    email: "",
    otp: "",
    loading: false,
    resendLoading: false,
    timeLeft: 60,
    verified: false,
  });

  const [sendingMail, setSendingMail] = useState(null);

  const token = localStorage.getItem("token");
  let isAdmin = false;
  try {
    const decoded = jwtDecode(token);
    isAdmin = decoded?.role === "admin";
  } catch (err) {
    isAdmin = false;
  }

  // --- DELETE ORDER ---
  const confirmDelete = (orderId) =>
    setDeleteState({ isOpen: true, loading: false, orderId });

  const handleDelete = async () => {
    const { orderId } = deleteState;
    if (!orderId) return;
    setDeleteState((prev) => ({ ...prev, loading: true }));
    try {
      await axiosInstance.delete(`/api/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: "Order deleted successfully", status: "success" });
      fetchOrders();
    } catch (err) {
      toast({
        title: "Failed to delete order",
        description: err.response?.data?.message || "Something went wrong",
        status: "error",
      });
    } finally {
      setDeleteState({ isOpen: false, loading: false, orderId: null });
    }
  };

  // --- OTP TIMERS ---
  useEffect(() => {
    if (!otpModal.open && !bulkOtpModal.open) return;

    if (otpModal.open) {
      setOtpModal((prev) => ({ ...prev, timeLeft: 60, verified: false }));
      const timer = setInterval(() => {
        setOtpModal((prev) => {
          if (prev.timeLeft <= 1) {
            clearInterval(timer);
            return { ...prev, timeLeft: 0 };
          }
          return { ...prev, timeLeft: prev.timeLeft - 1 };
        });
      }, 1000);
      return () => clearInterval(timer);
    } else if (bulkOtpModal.open) {
      setBulkOtpModal((prev) => ({ ...prev, timeLeft: 60, verified: false }));
      const timer = setInterval(() => {
        setBulkOtpModal((prev) => {
          if (prev.timeLeft <= 1) {
            clearInterval(timer);
            return { ...prev, timeLeft: 0 };
          }
          return { ...prev, timeLeft: prev.timeLeft - 1 };
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [otpModal.open, bulkOtpModal.open]);

  // --- SEND OTP ---
  const handleSendOtp = async (orderId, orderDetailId, phone) => {
    setOtpModal({
      open: true,
      type: "delivery",
      orderId,
      orderDetailId,
      phone,
      otp: "",
      loading: true,
      resendLoading: false,
      timeLeft: 60,
      verified: false,
    });

    try {
      await axiosInstance.post("/api/auth/send-otp", {
        orderId,
        orderDetailId,
        phone,
      });
      toast({ title: "OTP Sent", status: "success" });
      setOtpModal((prev) => ({ ...prev, loading: false }));
    } catch (err) {
      toast({
        title: "Error sending OTP",
        description: err.response?.data?.message || "Server error",
        status: "error",
      });
      setOtpModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleSendBulkOtp = async (orderId, phone) => {
    setBulkOtpModal({
      open: true,
      orderId,
      phone,
      otp: "",
      loading: true,
      resendLoading: false,
      timeLeft: 60,
      verified: false,
    });

    try {
      await axiosInstance.post("/api/orders/send-bulk-delivery-otp", {
        orderId,
        phone,
      });
      toast({ title: "Bulk OTP Sent", status: "success" });
      setBulkOtpModal((prev) => ({ ...prev, loading: false }));
    } catch (err) {
      toast({
        title: "Error sending Bulk OTP",
        description: err.response?.data?.message || "Server error",
        status: "error",
      });
      setBulkOtpModal((prev) => ({ ...prev, loading: false }));
    }
  };

  // --- VERIFY OTP ---
  const handleOtpSubmit = async () => {
    const { orderId, orderDetailId, otp } = otpModal;
    setOtpModal((prev) => ({ ...prev, loading: true }));
    try {
      await axiosInstance.post("/api/orders/verify-delivery-otp", {
        orderId,
        orderDetailId,
        otp,
      });
      toast({ title: "Delivery OTP Verified", status: "success" });
      setOtpModal((prev) => ({ ...prev, verified: true, open: false }));
      fetchOrders();
    } catch (err) {
      toast({ title: "Incorrect OTP", status: "error" });
    } finally {
      setOtpModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleBulkOtpSubmit = async () => {
    const { orderId, otp } = bulkOtpModal;
    setBulkOtpModal((prev) => ({ ...prev, loading: true }));
    try {
      await axiosInstance.post("/api/orders/verify-bulk-delivery-otp", {
        orderId,
        otp,
      });
      toast({ title: "Bulk Delivery OTP Verified", status: "success" });
      setBulkOtpModal((prev) => ({ ...prev, verified: true, open: false }));
      fetchOrders();
    } catch (err) {
      toast({ title: "Incorrect OTP", status: "error" });
    } finally {
      setBulkOtpModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleResendOtp = async () => {
    const { orderId, orderDetailId } = otpModal;
    setOtpModal((prev) => ({ ...prev, resendLoading: true }));
    try {
      await axiosInstance.post("/api/orders/resend-delivery-otp", {
        orderId,
        orderDetailId,
      });
      toast({ title: "OTP resent", status: "info" });
      setOtpModal((prev) => ({ ...prev, timeLeft: 60 }));
    } catch (err) {
      toast({ title: "Failed to resend OTP", status: "error" });
    } finally {
      setOtpModal((prev) => ({ ...prev, resendLoading: false }));
    }
  };

  const handleResendBulkOtp = async () => {
    const { orderId } = bulkOtpModal;
    setBulkOtpModal((prev) => ({ ...prev, resendLoading: true }));
    try {
      await axiosInstance.post("/api/orders/resend-bulk-delivery-otp", {
        orderId,
      });
      toast({ title: "Bulk OTP resent", status: "info" });
      setBulkOtpModal((prev) => ({ ...prev, timeLeft: 60 }));
    } catch (err) {
      toast({ title: "Failed to resend Bulk OTP", status: "error" });
    } finally {
      setBulkOtpModal((prev) => ({ ...prev, resendLoading: false }));
    }
  };

  // --- MASTER STATUS & RECEPTIONIST STATUS ---
  const handleMasterStatusChange = async (orderId, orderDetailId, newStatus) => {
    try {
      await axiosInstance.put(`/api/orders/${orderId}/status`, {
        orderDetailId,
        status: newStatus,
      });
      toast({ title: "Master Status updated", status: "success" });
      fetchOrders();
    } catch (err) {
      toast({ title: "Failed to update Master Status", status: "error" });
    }
  };

  const handleToggleDeliveryByAdmin = async (orderId, orderDetailId) => {
  try {
    await axiosInstance.put(`/api/orders/${orderId}/toggle-delivery`, {
      orderDetailId,
    });
    toast({ title: "Delivery status updated", status: "success" });
    fetchOrders();
  } catch (err) {
    toast({ title: "Failed to update delivery", status: "error" });
  }
};


  const handleReceptionistStatusChange = async (orderId, newStatus) => {
    try {
      await axiosInstance.put(`/api/orders/update-receptionist-status`, {
        orderId,
        newStatus,
      });
      toast({ title: "Receptionist Status Updated", status: "success" });
      fetchOrders();
    } catch (err) {
      toast({ title: "Failed to update Receptionist Status", status: "error" });
    }
  };

  // --- SEND COMPLETE MAIL ---
  const handleSendCompleteMail = async (orderId, phone) => {
    setSendingMail(orderId);
    try {
      await axiosInstance.post("/api/orders/send-pickup-email", { orderId, phone });
      toast({ title: "Pickup SMS Sent", status: "success" });
      fetchOrders();
    } catch (err) {
      toast({ title: "Failed to send mail", status: "error" });
    } finally {
      setSendingMail(null);
    }
  };

  // --- MARK PAYMENT AS PAID ---
  const handleSetPendingToZero = async (orderId) => {
    try {
      await axiosInstance.put("/api/orders/update-pending", {
        orderId,
        pendingAmount: 0,
      });
      toast({ title: "Marked as paid", status: "success" });
      fetchOrders();
    } catch (err) {
      toast({ title: "Failed to update", status: "error" });
    }
  };

  // --- CHECK BULK OTP ELIGIBILITY ---
  const isOrderEligibleForBulkOtp = (order) => {
    return (
      order.orderDetails.every(item => item.measurements?.status === "done") &&
      order.payment.pending <= 0 &&
      order.orderDetails.some(item => !item.isDelivered)
    );
  };

  return (
    <>
      <Box overflowX="auto" border="1px solid #e2e8f0" borderRadius="md">
        <Table size="sm">
          <Thead bg="gray.100">
            <Tr>
              <Th>S.No</Th>
              <Th>Bill No</Th>
              <Th>Items</Th>
              <Th>Order Quantity</Th>
              <Th>Master</Th>
              <Th>Master Status</Th>
              <Th>Date</Th>
              <Th>Customer</Th>
              <Th>Phone</Th>
              <Th>Receptionist Status</Th>
              <Th>Delivery Date</Th>
              <Th>Pending</Th>
              <Th>Action</Th>
              <Th>Item OTP</Th>
              <Th>Bulk OTP</Th>
              <Th>Complete SMS</Th>
              {isAdmin && <Th>Admin Deliver</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {orderLoading && (
              <Tr>
                <Td colSpan={isAdmin ? 16 : 15} textAlign="center">
                  <Spinner />
                </Td>
              </Tr>
            )}
            {data.map((order, i) => {
              const { billNumber, customer, payment, orderDetails, receptionistStatus } = order;
              return (
                <Tr key={order._id}>
                  <Td>{i + 1}</Td>
                  <Td>{billNumber}</Td>
                  <Td>{orderDetails.map((d) => d.item).join(", ")}</Td>
                  <Td> {orderDetails.map((item) => (
                      <Box key={item._id}>
                        <Text fontSize="xs">{item.item}</Text>
                        <Text fontSize="xs">Qty: {item.quantity}</Text>
                      </Box>
                    ))}</Td>
                  <Td>{orderDetails.map((d) => d.tailor?.name || "N/A").join(", ")}</Td>
                  <Td>
                    {orderDetails.map((item) => (
                      <Box key={item._id}>
                        <Text fontSize="xs">{item.item}</Text>
                        <Select
                          size="xs"
                          value={item.measurements?.status || "in-progress"}
                          onChange={(e) =>
                            handleMasterStatusChange(order._id, item._id, e.target.value)
                          }
                        >
                          <option value="in-progress">In Progress</option>
                          <option value="done">Done</option>
                          <option value="cancel">Cancel</option>
                        </Select>
                      </Box>
                    ))}
                  </Td>
                  <Td>{new Date(order.orderDate).toLocaleDateString("en-GB")}</Td>
                  <Td>{customer?.name || "N/A"}</Td>
                  <Td>{customer?.phone || "N/A"}</Td>
                  <Td>
                    <Select
                      size="xs"
                      value={receptionistStatus}
                      onChange={(e) =>
                        handleReceptionistStatusChange(order._id, e.target.value)
                      }
                    >
                      <option value="Pending">Pending</option>
                      <option value="Done">Done</option>
                      <option value="Delivered">Delivered</option>
                    </Select>
                  </Td>
                  <Td>
                    {orderDetails.map((item) => (
                      <Box key={item._id}>
                        {item.deliveryDate
                          ? new Date(item.deliveryDate).toLocaleDateString("en-GB")
                          : "—"}
                      </Box>
                    ))}
                  </Td>
                  <Td>
                    <Button
                      size="xs"
                      colorScheme={payment.pending > 0 ? "red" : "green"}
                      variant="outline"
                      onClick={() => handleSetPendingToZero(order._id)}
                      isDisabled={payment.pending === 0}
                    >
                      {payment.pending > 0
                        ? `Mark as Paid (₹${payment.pending})`
                        : "Paid"}
                    </Button>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <Link to={`/dashboard/update-order/${order._id}`}>
                        <IconButton icon={<FaEye />} size="sm" />
                      </Link>
                      <IconButton
                        icon={<FaTrash />}
                        size="sm"
                        onClick={() => confirmDelete(order._id)}
                      />
                    </HStack>
                  </Td>
                  {/* ITEM OTP */}
                  <Td>
                    {orderDetails.map((item) => {
                      const eligible =
                        item.measurements?.status === "done" &&
                        payment.pending <= 0;

                      return (
                        <Box key={item._id}>
                          <Text fontSize="xs">{item.item}</Text>
                          {eligible ? (
                            item.isDelivered ? (
                              <Text fontSize="xs" color="green.500">
                                Delivered
                              </Text>
                            ) : (
                              <Text
                                as="u"
                                fontSize="xs"
                                cursor="pointer"
                                color="orange.500"
                                onClick={() =>
                                  handleSendOtp(order._id, item._id, customer.phone)
                                }
                              >
                                Send OTP
                              </Text>
                            )
                          ) : (
                            <Text fontSize="xs" color="gray.400">
                              Not Eligible
                            </Text>
                          )}
                        </Box>
                      );
                    })}
                  </Td>
                  {/* BULK OTP */}
                  <Td>
                    {isOrderEligibleForBulkOtp(order) ? (
                      order.orderDetails.every(item => item.isDelivered) ? (
                        <Text fontSize="xs" color="green.500">
                          All Delivered
                        </Text>
                      ) : (
                        <Text
                          as="u"
                          fontSize="xs"
                          cursor="pointer"
                          color="blue.500"
                          onClick={() =>
                            handleSendBulkOtp(order._id, customer.phone)
                          }
                        >
                          Send Bulk OTP
                        </Text>
                      )
                    ) : (
                      <Text fontSize="xs" color="gray.400">
                        Not Eligible
                      </Text>
                    )}
                  </Td>
                  {/* COMPLETE MAIL */}
                  <Td>
                    {orderDetails.every((item) => item.isDelivered) ? (
                      <Text fontSize="sm" color="green.600">
                        All Delivered
                      </Text>
                    ) : (
                      <Button
                        size="xs"
                        colorScheme="blue"
                        isLoading={sendingMail === order._id}
                        onClick={() =>
                          handleSendCompleteMail(order._id, customer.phone)
                        }
                      >
                        Send SMS
                      </Button>
                    )}
                  </Td>
                  {isAdmin && (
                    <Td>
                      {orderDetails.map((item) => (
                        <Box key={item._id}>
                          <Text fontSize="xs">{item.item}</Text>
                          <Button
                            size="xs"
                            variant="outline"
                            colorScheme={item.isDelivered ? "green" : "orange"}
                            onClick={() => handleToggleDeliveryByAdmin(order._id, item._id)}
                          >
                            {item.isDelivered ? "Delivered" : "Mark Delivered"}
                          </Button>
                        </Box>
                      ))}
                    </Td>
                  )}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>

      {/* DELETE ALERT */}
      <AlertDialog
        isOpen={deleteState.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={() =>
          setDeleteState({ isOpen: false, loading: false, orderId: null })
        }
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Delete Order</AlertDialogHeader>
            <AlertDialogBody>
              Are you sure? This cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() =>
                  setDeleteState({ isOpen: false, loading: false, orderId: null })
                }
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDelete}
                isLoading={deleteState.loading}
                ml={3}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* ITEM OTP MODAL */}
      <OTPModal
        isOpen={otpModal.open}
        onClose={() => setOtpModal((prev) => ({ ...prev, open: false }))}
        verified={otpModal.verified}
        otp={otpModal.otp}
        setOtp={(value) => setOtpModal((prev) => ({ ...prev, otp: value }))}
        resendLoading={otpModal.resendLoading}
        timeLeft={otpModal.timeLeft}
        handleOtpSubmit={handleOtpSubmit}
        handleResendOtp={handleResendOtp}
        email={otpModal.email}
        orderId={otpModal.orderId}
        isLoading={otpModal.loading}
        setIsLoading={(value) =>
          setOtpModal((prev) => ({ ...prev, loading: value }))
        }
        title="Item Delivery OTP Verification"
      />

      {/* BULK OTP MODAL */}
      <OTPModal
        isOpen={bulkOtpModal.open}
        onClose={() => setBulkOtpModal((prev) => ({ ...prev, open: false }))}
        verified={bulkOtpModal.verified}
        otp={bulkOtpModal.otp}
        setOtp={(value) => setBulkOtpModal((prev) => ({ ...prev, otp: value }))}
        resendLoading={bulkOtpModal.resendLoading}
        timeLeft={bulkOtpModal.timeLeft}
        handleOtpSubmit={handleBulkOtpSubmit}
        handleResendOtp={handleResendBulkOtp}
        email={bulkOtpModal.email}
        orderId={bulkOtpModal.orderId}
        isLoading={bulkOtpModal.loading}
        setIsLoading={(value) =>
          setBulkOtpModal((prev) => ({ ...prev, loading: value }))
        }
        title="Bulk Delivery OTP Verification"
      />
    </>
  );
};

export default OrdersTable;
